/**
 * Analytics Manager
 *
 * Provides a unified interface for tracking analytics across multiple providers
 * Manages the transition from Firebase Analytics to Firestore for analytics
 */

import * as firebaseAnalytics from './firebase'
import * as dbAnalytics from './dbAnalytics'
import { GameEvents } from './gameAnalytics'
import { WordLength } from '../types/game'

// Feature flag for enabling Firestore analytics
const USE_FIRESTORE = import.meta.env.VITE_USE_FIRESTORE === 'true'

/**
 * Track a custom event across all enabled analytics providers
 *
 * @param eventName - The name of the event to track
 * @param eventParams - Additional parameters to track with the event
 */
export const trackEvent = (
  eventName: string,
  eventParams: Record<string, string | number | boolean | null> = {}
) => {
  // Always track to Firebase Analytics (legacy)
  firebaseAnalytics.trackEvent(
    eventName,
    eventParams as Record<string, string | number | boolean>
  )

  // Track to Firestore if enabled
  if (USE_FIRESTORE) {
    dbAnalytics.trackEventToFirestore(eventName, eventParams)
  }
}

/**
 * Set user properties for analytics segmentation
 *
 * @param properties - User properties to set
 */
export const setUserProperties = (properties: Record<string, string>) => {
  // Always set in Firebase
  firebaseAnalytics.setAnalyticsUserProperties(properties)
}

// Core Gameplay Metrics

/**
 * Track when a game is started
 */
export const trackGameStart = (wordLength: WordLength) => {
  trackEvent(GameEvents.GAME_STARTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when a game is completed (won or lost)
 */
export const trackGameCompletion = (
  wordLength: WordLength,
  won: boolean,
  attempts: number,
  timeTaken: number
) => {
  trackEvent(GameEvents.GAME_COMPLETED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    time_taken_seconds: Math.floor(timeTaken / 1000),
    timestamp: Date.now(),
  })
}

/**
 * Track when a player abandons a game
 */
export const trackGameAbandoned = (
  wordLength: WordLength,
  attemptsBeforeAbandoning: number
) => {
  trackEvent(GameEvents.GAME_ABANDONED, {
    word_length: wordLength,
    attempts_before_abandoning: attemptsBeforeAbandoning,
    timestamp: Date.now(),
  })
}

/**
 * Track when a guess is submitted
 */
export const trackGuessSubmitted = (
  wordLength: WordLength,
  guessNumber: number,
  correct: boolean
) => {
  trackEvent(GameEvents.GUESS_SUBMITTED, {
    word_length: wordLength,
    guess_number: guessNumber,
    correct: correct,
    timestamp: Date.now(),
  })
}

// User Preference Metrics

/**
 * Track when a user selects a game mode
 */
export const trackModeSelected = (wordLength: WordLength) => {
  trackEvent(GameEvents.MODE_SELECTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Set user device information as user properties
 */
export const setDeviceProperties = () => {
  // Only track information that's useful for UI optimization
  const properties: Record<string, string> = {
    device_type: isMobile() ? 'mobile' : 'desktop',
    viewport_width: `${window.innerWidth}px`,
    preferred_mode: localStorage.getItem('preferredMode') || 'unknown',
  }

  setUserProperties(properties)
}

// Engagement Indicators

/**
 * Track when a user shares their result
 */
export const trackResultShared = (
  wordLength: WordLength,
  won: boolean,
  attempts: number
) => {
  trackEvent(GameEvents.RESULT_SHARED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user switches to another challenge
 */
export const trackChallengeSwitch = (
  fromWordLength: WordLength,
  toWordLength: WordLength
) => {
  trackEvent(GameEvents.SWITCH_CHALLENGE, {
    from_word_length: fromWordLength,
    to_word_length: toWordLength,
    timestamp: Date.now(),
  })
}

/**
 * Initialize the analytics systems
 */
export const initAnalytics = () => {
  // Initialize Firestore if enabled
  if (USE_FIRESTORE) {
    dbAnalytics.initDatabaseAnalytics()
  }
}

// Helper Functions

/**
 * Simple mobile detection
 */
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}

export default {
  trackEvent,
  setUserProperties,
  trackGameStart,
  trackGameCompletion,
  trackGameAbandoned,
  trackGuessSubmitted,
  trackModeSelected,
  setDeviceProperties,
  trackResultShared,
  trackChallengeSwitch,
  initAnalytics,
}
